import { Component, OnInit, AfterViewInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { FreightManagerService } from 'src/app/services/freight-manager/freight-manager.service';
import { FreightManagerModalService } from 'src/app/services/freight-manager-modal/freight-manager-modal.service';
import { ShippingLineService } from 'src/app/services/shipping-line/shipping-line.service';
import { SettingService } from 'src/app/services/setting/setting.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { BookingAgentService } from 'src/app/services/booking-agent/booking-agent.service';
import { CopyFreightManagerComponent } from 'src/app/layout/modals/shipping_and_documents/copy-freight-manager/copy-freight-manager.component';
import Swal from 'sweetalert2';

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-freight-manager-new',
  templateUrl: './freight-manager-new.component.html',
  styleUrls: ['./freight-manager-new.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class FreightManagerNewComponent extends DestructorComponent implements OnInit, AfterViewInit {

  @ViewChild(CopyFreightManagerComponent) copyFreightManagerModal!: CopyFreightManagerComponent;

  // Data properties
  freightManagers: any[] = [];
  isLoading = false;

  // Tab functionality
  activeTab: string = 'roro';

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedFreightManagers: string[] = [];

  // Dynamic columns - shipping lines
  shippingLines: any[] = [];
  isLoadingShippingLines = false;

  // Booking agents
  bookingAgents: any[] = [];
  isLoadingBookingAgents = false;

  // Multi-row editing properties
  hasChanges: boolean = false;
  changedRows: any[] = [];
  formData: { [freightManagerId: string]: any } = {};

  // Freight Buffer form properties
  freightBufferForm!: FormGroup;
  isLoadingFreightBuffer = false;
  settingId: string = '';

  // Month-Year filter
  monthYearOptions: { value: string; label: string }[] = [];
  selectedMonthYear: string = '';

  constructor(
    private fb: FormBuilder,
    private _freightManagerService: FreightManagerService,
    private _freightManagerModalService: FreightManagerModalService,
    private _shippingLineService: ShippingLineService,
    private _settingService: SettingService,
    private _globalStateService: GlobalStateService,
    private _bookingAgentService: BookingAgentService,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.initializeFreightBufferForm();
    this.initializeMonthYearOptions();
  }

  ngOnInit(): void {
    this.loadShippingLines();
    this.loadBookingAgents();

    // Set initial month_year filter to current month
    this.filters = { month_year: this.selectedMonthYear };
    console.log('ngOnInit - selectedMonthYear:', this.selectedMonthYear);
    console.log('ngOnInit - filters:', this.filters);

    this.loadFreightManagers(1, '', this.filters);
    this.loadFreightBufferData();
  }

  ngAfterViewInit(): void {
    // Initialize menus after view is initialized
    this.initializeMenus();
  }

  // Initialize KTMenu instances
  initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Tab functionality
  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.filters = { ...this.filters, type: tab };
    this.loadFreightManagers(1, this.searchTerm, this.filters);
  }

  loadShippingLines(): void {
    this.isLoadingShippingLines = true;
    this._shippingLineService.GetData('1', '', { paginate: false }).subscribe({
      next: (response) => {
        if (response.status) {
          this.shippingLines = response.data || [];
        } else {
          this.shippingLines = [];
        }
        this.isLoadingShippingLines = false;
      },
      error: (error) => {
        console.error('Error loading shipping lines:', error);
        this.shippingLines = [];
        this.isLoadingShippingLines = false;
      }
    });
  }

  loadBookingAgents(): void {
    this.isLoadingBookingAgents = true;
    this._bookingAgentService.GetData('1', '', { paginate: false }).subscribe({
      next: (response) => {
        if (response.status) {
          this.bookingAgents = response.data || [];
        } else {
          this.bookingAgents = [];
        }
        this.isLoadingBookingAgents = false;
      },
      error: (error) => {
        console.error('Error loading booking agents:', error);
        this.bookingAgents = [];
        this.isLoadingBookingAgents = false;
      }
    });
  }

  loadFreightManagers(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    // Add type filter based on active tab
    const finalFilters = { ...filters, type: this.activeTab };
    console.log('loadFreightManagers - finalFilters:', finalFilters);

    this._freightManagerService.GetData(page.toString(), search, finalFilters)
      .subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status) {
            this.freightManagers = response.data.data || [];
            this.paginationInfo = response.data.pagination || this.resetPaginationInfo();
            // Initialize form data for new freight managers
            this.initializeFormData();
          } else {
            this.freightManagers = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading freight managers:', error);
          this.freightManagers = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  onPageChange(page: number): void {
    this.loadFreightManagers(page, this.searchTerm, this.filters);
  }

  onMonthYearChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedMonthYear = value;

    // Add month_year to filters and reload data
    if (value) {
      this.filters = { ...this.filters, month_year: value };
    } else {
      const { month_year, ...restFilters } = this.filters;
      this.filters = restFilters;
    }

    this.loadFreightManagers(1, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadFreightManagers(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadFreightManagers();
  }

  openAddFreightManagerModal(): void {
    this._freightManagerModalService.clearSelectedFreightManager();
    const modalElement = document.getElementById('kt_modal_freight_manager_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(freightManager: any): void {
    this._freightManagerModalService.setSelectedFreightManager(freightManager);
    const modalElement = document.getElementById('kt_modal_freight_manager_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openCopyDataModal(): void {
    // Set the activeTab on the child component before showing the modal
    if (this.copyFreightManagerModal) {
      this.copyFreightManagerModal.activeTab = this.activeTab;
    }

    const modalElement = document.getElementById('kt_modal_copy_freight_manager');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteFreightManager(freightManager: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete this freight manager record. This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(freightManager);
      }
    });
  }

  private performDelete(freightManager: any): void {
    this._freightManagerService.DeleteDataById(freightManager._id)
      .subscribe({
        next: (response) => {
          if (response.status) {
            Swal.fire('Deleted!', 'Freight manager has been deleted.', 'success');
            this.loadFreightManagers(this.currentPage, this.searchTerm, this.filters);
          } else {
            Swal.fire('Error!', response.message || 'Failed to delete freight manager.', 'error');
          }
        },
        error: (error) => {
          console.error('Error deleting freight manager:', error);
          Swal.fire('Error!', 'An error occurred while deleting the freight manager.', 'error');
        }
      });
  }

  bulkDelete(): void {
    if (this.selectedFreightManagers.length === 0) {
      Swal.fire('Warning!', 'Please select freight managers to delete.', 'warning');
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${this.selectedFreightManagers.length} freight manager(s). This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!'
    }).then((result) => {
      if (result.isConfirmed) {
        this._freightManagerService.BulkDelete(this.selectedFreightManagers)
          .subscribe({
            next: (response) => {
              if (response.status) {
                Swal.fire('Deleted!', 'Selected freight managers have been deleted.', 'success');
                this.selectedFreightManagers = [];
                this.loadFreightManagers(this.currentPage, this.searchTerm, this.filters);
              } else {
                Swal.fire('Error!', response.message || 'Failed to delete freight managers.', 'error');
              }
            },
            error: (error) => {
              console.error('Error bulk deleting freight managers:', error);
              Swal.fire('Error!', 'An error occurred while deleting the freight managers.', 'error');
            }
          });
      }
    });
  }

  // Event handlers for modal events
  onFreightManagerAdded(): void {
    console.log('Freight manager added, refreshing list...');
    this.loadFreightManagers(this.currentPage, this.searchTerm, this.filters);
  }

  onFreightManagerUpdated(): void {
    console.log('Freight manager updated, refreshing list...');
    this.loadFreightManagers(this.currentPage, this.searchTerm, this.filters);
  }

  onDataCopied(): void {
    console.log('Freight manager data copied, refreshing list...');
    this.loadFreightManagers(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  private resetPaginationInfo(): PaginationInfo {
    return {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(freightManagerId: string): boolean {
    return this.selectedFreightManagers.includes(freightManagerId);
  }

  toggleSelection(freightManagerId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedFreightManagers.includes(freightManagerId)) {
        this.selectedFreightManagers.push(freightManagerId);
      }
    } else {
      this.selectedFreightManagers = this.selectedFreightManagers.filter(id => id !== freightManagerId);
    }
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedFreightManagers = this.freightManagers.map(fm => fm._id);
    } else {
      this.selectedFreightManagers = [];
    }
  }

  isAllSelected(): boolean {
    return this.freightManagers.length > 0 && this.selectedFreightManagers.length === this.freightManagers.length;
  }

  isIndeterminate(): boolean {
    return this.selectedFreightManagers.length > 0 && this.selectedFreightManagers.length < this.freightManagers.length;
  }

  // Form data management methods
  private initializeFormData(): void {
    this.formData = {};
    this.freightManagers.forEach(freightManager => {
      this.formData[freightManager._id] = {
        booking_agent_id: freightManager.booking_agent_id || '',
        shipping_lines: {}
      };

      // Initialize shipping line data for each freight manager
      this.shippingLines.forEach(shippingLine => {
        this.formData[freightManager._id].shipping_lines[shippingLine._id] = {
          under_20m3: freightManager.shipping_lines?.[shippingLine._id]?.under_20m3 || '',
          over_20m3: freightManager.shipping_lines?.[shippingLine._id]?.over_20m3 || '',
          validity_upto: freightManager.shipping_lines?.[shippingLine._id]?.validity_upto || '',
          name: shippingLine.name
        };
      });
    });
  }

  // Helper method to extract value from input event
  getInputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  onFieldChange(freightManagerId: string, field: string, value: any, shippingLineId?: string): void {
    if (!this.formData[freightManagerId]) {
      this.formData[freightManagerId] = {
        booking_agent_id: '',
        shipping_lines: {}
      };
    }

    if (shippingLineId) {
      // Handle shipping line field changes
      if (!this.formData[freightManagerId].shipping_lines[shippingLineId]) {
        this.formData[freightManagerId].shipping_lines[shippingLineId] = {
          under_20m3: '',
          over_20m3: '',
          validity_upto: '',
          name: this.shippingLines.find(sl => sl._id === shippingLineId)?.name || ''
        };
      }
      this.formData[freightManagerId].shipping_lines[shippingLineId][field] = value;
    } else {
      // Handle direct field changes (like booking_agent_id)
      this.formData[freightManagerId][field] = value;
    }

    // Check if this freight manager is already in changed rows
    const existingIndex = this.changedRows.findIndex(row => row.freight_manager_id === freightManagerId);

    if (existingIndex >= 0) {
      // Update existing changed row
      this.changedRows[existingIndex] = {
        freight_manager_id: freightManagerId,
        data: { ...this.formData[freightManagerId] }
      };
    } else {
      // Add new changed row
      this.changedRows.push({
        freight_manager_id: freightManagerId,
        data: { ...this.formData[freightManagerId] }
      });
    }

    this.hasChanges = this.changedRows.length > 0;
  }

  getFRate(freightManager: any, key: string): number | string {

    if (!freightManager.shipping_lines) return "-"

    let max = 0;

    for (let shipping_key in freightManager.shipping_lines) {
      if (freightManager.shipping_lines[shipping_key][key] && +freightManager.shipping_lines[shipping_key][key] > max) {
        max = +freightManager.shipping_lines[shipping_key][key]
      }
    }

    return max;
  }

  getCRate(freightManager: any, key: string): number | string {

    let min = 0;

    if (freightManager && !freightManager.shipping_lines) return "-"

    if (freightManager.shipping_lines) {
      for (let shipping_key in freightManager.shipping_lines) {
        if (!min && freightManager.shipping_lines[shipping_key][key]) {
          min = +freightManager.shipping_lines[shipping_key][key]
          continue;
        }
        if (freightManager.shipping_lines[shipping_key][key] && +freightManager.shipping_lines[shipping_key][key] < min) {
          min = +freightManager.shipping_lines[shipping_key][key]
        }
      }
    }

    return min;
  }

  onSave(): void {
    if (this.changedRows.length === 0) {
      return;
    }

    const changedCount = this.changedRows.length;

    console.log('Saving changes for freight managers:', this.changedRows);

    this._freightManagerService.BulkUpdate({ data: this.changedRows }).subscribe({
      next: (response) => {
        console.log('Update response:', response);
        this.isLoading = false;
        Swal.fire({
          title: 'Changes Logged',
          text: `${changedCount} freight manager(s) data updated successfully.`,
          icon: 'success'
        });

        // Reset changes after logging
        this.hasChanges = false;
        this.changedRows = [];
      },
      error: (error) => {
        console.error('Error updating freight managers:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to update freight managers. Please try again.',
          icon: 'error'
        });
        this.isLoading = false;
      }
    });

    // For now, just log the data as requested

  }

  // Freight Buffer methods
  private initializeFreightBufferForm(): void {
    this.freightBufferForm = this.fb.group({
      freight_buffer: ['', [Validators.required]]
    });
  }

  private initializeMonthYearOptions(): void {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear()-1;
    const currentMonth = currentDate.getMonth(); // 0-11

    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Generate options for current year (from January to December)
    for (let month = 0; month < 12; month++) {
      const monthName = months[month];
      const value = `${currentYear}-${String(month + 1).padStart(2, '0')}`;
      this.monthYearOptions.push({
        value: value,
        label: `${monthName} ${currentYear}`
      });
    }

    // Generate options for next year (from January to December)
    const nextYear = currentYear + 1;
    for (let month = 0; month < 12; month++) {
      const monthName = months[month];
      const value = `${nextYear}-${String(month + 1).padStart(2, '0')}`;
      this.monthYearOptions.push({
        value: value,
        label: `${monthName} ${nextYear}`
      });
    }

    // Set current month-year as default
    this.selectedMonthYear = `${currentYear+1}-${String(currentMonth + 1).padStart(2, '0')}`;
    console.log('Initialized selectedMonthYear:', this.selectedMonthYear);
    console.log('Month-Year options:', this.monthYearOptions);
  }

  private loadFreightBufferData(): void {
    this.subscriptions.push(
      this._globalStateService.GetSetting().subscribe((res: any) => {
        if (res) {
          this.settingId = res._id;

          // Check if freight_buffer exists in settings
          if (res.freight_buffer !== undefined) {
            this.freightBufferForm.patchValue({
              freight_buffer: res.freight_buffer
            });
          }
        }
      })
    );
  }

  onFreightBufferSubmit(): void {
    if (this.freightBufferForm.valid) {
      this.isLoadingFreightBuffer = true;

      const formData = this.freightBufferForm.getRawValue();

      const submitData = {
        freight_buffer: formData.freight_buffer
      };

      console.log('Submitting freight buffer:', submitData);

      this.subscriptions.push(
        this._settingService.UpdateData(this.settingId, submitData).subscribe({
          next: (response: any) => {
            this.isLoadingFreightBuffer = false;
            console.log('Freight buffer updated successfully:', response);
            this._globalStateService.SetSetting();

            Swal.fire({
              text: "Freight buffer has been successfully updated!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          },
          error: (error) => {
            this.isLoadingFreightBuffer = false;
            console.error('Error updating freight buffer:', error);
            Swal.fire({
              text: "Error updating freight buffer. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    }
  }

  // Export methods
  exportCSV(): void {
    console.log('Exporting CSV for freight managers...');

    if (this.freightManagers.length === 0) {
      Swal.fire({
        text: "No data available to export.",
        icon: "warning",
        buttonsStyling: false,
        confirmButtonText: "Ok, got it!",
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
      return;
    }

    this.generateCSV(false);
  }

  exportCSVWithBuffer(): void {
    console.log('Exporting CSV with buffer for freight managers...');

    if (this.freightManagers.length === 0) {
      Swal.fire({
        text: "No data available to export.",
        icon: "warning",
        buttonsStyling: false,
        confirmButtonText: "Ok, got it!",
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
      return;
    }

    // Get current freight buffer value
    const freightBuffer = this.freightBufferForm.get('freight_buffer')?.value || 0;

    if (!freightBuffer || freightBuffer <= 0) {
      Swal.fire({
        text: "Please set a valid freight buffer value before exporting with buffer.",
        icon: "warning",
        buttonsStyling: false,
        confirmButtonText: "Ok, got it!",
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
      return;
    }

    this.generateCSV(true, freightBuffer);
  }

  private generateCSV(includeBuffer: boolean, bufferValue?: number): void {
    try {
      // Prepare CSV headers
      const headers = [
        'From Country',
        'To Country',
        'To Port',
        'Booking Agent',
        'Shipper',
        'Region',
        'Type',
        'Memo'
      ];

      // Add shipping line headers
      this.shippingLines.forEach(shippingLine => {

        if (includeBuffer && bufferValue) {
          headers.push(`${shippingLine.name} - Under 20M3 + Buffer`);
          headers.push(`${shippingLine.name} - Over 20M3 + Buffer`);
        }
        else {
          headers.push(`${shippingLine.name} - Under 20M3`);
          headers.push(`${shippingLine.name} - Over 20M3`);
        }
        headers.push(`${shippingLine.name} - Validity`);
      });

      // Prepare CSV rows
      const csvRows = [headers.join(',')];

      this.freightManagers.forEach(freightManager => {
        const bookingAgent = this.bookingAgents.find(ba => ba._id === freightManager.booking_agent_id);
        const row = [
          this.escapeCSVField(freightManager.from_country?.name || ''),
          this.escapeCSVField(freightManager.to_country?.name || ''),
          this.escapeCSVField(freightManager.to_port?.name || ''),
          this.escapeCSVField(bookingAgent?.name || ''),
          this.escapeCSVField(freightManager.shipper?.name || ''),
          this.escapeCSVField(freightManager.region?.name || ''),
          this.escapeCSVField(freightManager.type || ''),
          this.escapeCSVField(freightManager.memo || '')
        ];

        // Add shipping line data
        this.shippingLines.forEach(shippingLine => {
          const shippingLineData = freightManager.shipping_lines?.[shippingLine._id];
          const under20m3 = parseFloat(shippingLineData?.under_20m3 || '0');
          const over20m3 = parseFloat(shippingLineData?.over_20m3 || '0');
          const validity = shippingLineData?.validity_upto || '';


          if (includeBuffer && bufferValue) {
            row.push((under20m3 + bufferValue).toString());
            row.push((over20m3 + bufferValue).toString());
          }
          else {
            row.push(under20m3.toString());
            row.push(over20m3.toString());
          }
          row.push(this.escapeCSVField(validity));

        });

        csvRows.push(row.join(','));
      });

      // Create and download CSV file
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);

        const fileName = includeBuffer
          ? `freight-managers-${this.activeTab}-with-buffer-${new Date().toISOString().split('T')[0]}.csv`
          : `freight-managers-${this.activeTab}-${new Date().toISOString().split('T')[0]}.csv`;

        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        Swal.fire({
          text: `CSV file has been downloaded successfully!`,
          icon: "success",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      }
    } catch (error) {
      console.error('Error generating CSV:', error);
      Swal.fire({
        text: "Error generating CSV file. Please try again.",
        icon: "error",
        buttonsStyling: false,
        confirmButtonText: "Ok, got it!",
        customClass: {
          confirmButton: "btn btn-primary"
        }
      });
    }
  }

  private escapeCSVField(field: string): string {
    if (field === null || field === undefined) {
      return '';
    }

    const stringField = field.toString();

    // If field contains comma, newline, or double quote, wrap in quotes and escape quotes
    if (stringField.includes(',') || stringField.includes('\n') || stringField.includes('"')) {
      return `"${stringField.replace(/"/g, '""')}"`;
    }

    return stringField;
  }
}
